import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SiteNav from '../components/SiteNav';
import { useTema } from '../hooks/useTema';

// icon + bullet count per entry — display copy itself lives in i18n/locales/*/news.json
// (entry_N_date/title/body/bullet_N), keyed by index to match this array.
const ENTRIES = [
  // 2026-09-18 — preferiti, didascalie e caricamenti che si fermano e
  // ripartono. Tre paragrafi, nessun elenco puntato: ogni paragrafo e' una
  // delle tre cose, e spezzarli in punti li renderebbe tutti uguali.
  // Voce nuova in cima: le altre sono state rinumerate (+1) in TUTTI E
  // CINQUE i news.json (it, en, es, fr, de).
  { icon: '⭐', bullets: 0 },
  // 2026-09-16 — lo spagnolo: sito, file manager ed email. Un paragrafo
  // piu' la riga sui legali ancora in inglese. Voce nuova in cima: le altre
  // rinumerate (+1) nei TRE news.json (it, en, es).
  { icon: '🇪🇸', bullets: 0 },
  // 2026-09-15 — il programma di invito (referral): link personale, -5 € per
  // chi lo usa, +100 GB per sempre a chi invita. Testo dell'operatore, cinque
  // paragrafi con due sotto-titoli ("Come funziona"/"Perché lo facciamo") resi
  // come paragrafi. Voce nuova in cima: le altre sono state rinumerate (+1) in
  // entrambi i news.json.
  { icon: '🤝', bullets: 0 },
  // 2026-09-14 — l'app Android pronta al rilascio (Play Store 1 ottobre, test
  // chiuso con 12 tester da oggi); prossimi passi Windows e Mac. Un paragrafo.
  { icon: '🤖', bullets: 0 },
  // 2026-09-14 — i 5 GB per file, il caricamento a blocchi con ripresa, il
  // pannello Trasferimenti, le cartelle intere. Un paragrafo solo, come l'ha
  // scritto l'operatore: niente elenco puntato.
  { icon: '⬆️', bullets: 0 },
  // 2026-09-12 — il recupero dell'accesso, acceso per chi ha la chiave di
  // recupero. Due paragrafi: il primo dice che funziona, il secondo dice a
  // chi la chiave non ce l'ha di generarla adesso — ed e' il paragrafo che
  // conta, perche' quella e' l'unica strada che li riporta dentro.
  { icon: '🔑', bullets: 0 },
  // 2026-09-12 — l'anteprima PDF. Nessun elenco puntato: il testo dice in
  // tre righe dove passano i byte, ed e' tutto quello che conta.
  { icon: '📄', bullets: 0 },
  // 2026-09-12 — l'app Android che si aggiorna da sola.
  { icon: '🤖', bullets: 0 },
  // 2026-09-07 — il 5 TB a numero chiuso. Nessun elenco puntato: il testo
  // spiega perche' i posti sono contati, e spezzarlo lo indebolirebbe.
  { icon: '🎟️', bullets: 0 },
  // 2026-09-06 — il collegamento del computer. Rimanda ai due articoli invece
  // di ripetere la guida: qui e' un annuncio, il seguito sta nell'articolo.
  { icon: '🖥️', bullets: 0, guide: true },
  // Le chiavi dei testi sono legate all'INDICE (entry_N_*), quindi una voce
  // nuova in cima obbliga a rinumerare tutte le altre nei due news.json.
  // Fatto il 2026-09-02 e di nuovo il 2026-09-04, quando e' entrata la voce
  // sulle anteprime: ogni volta va rinumerato tutto in entrambe le lingue.
  // 2026-09-04 — anteprime e lettori nel vault. Nessun elenco puntato: il
  // testo spiega il perche' tecnico e gli elenchi lo spezzerebbero.
  { icon: '🖼️', bullets: 0 },
  { icon: '🎨', bullets: 4 },
  { icon: '✨', bullets: 4 },
  { icon: '🗂️', bullets: 0 },
  { icon: '🕘', bullets: 4 },
  { icon: '🎁', bullets: 5 },
  { icon: '👪', bullets: 4 },
  { icon: '🎫', bullets: 0 },
  { icon: '🌐', bullets: 0 },
  { icon: '🤖', bullets: 0 },
];

export default function NewsPage({ onLogin, onRegister, onNavigateHome, onAbout, onTerms, onPrivacy, onApriGuida}) {
  const { t } = useTranslation(['news','guide']);
  // Stessa definizione usata da home e Chi siamo: hooks/useTema.js.
  const { tema, cambiaTema } = useTema();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  function goHome(hash) { return onNavigateHome(hash); }

  // Le voci di sezione sono ancore della home: da qui si naviga, non si scorre.
  function vaiA(id) {
    return goHome(id === 'hero' ? undefined : id);
  }

  return (
    // cps-home-root porta con se' la palette chiara (App.css ~riga 2253). Il
    // nome resta quello della home finche' lo scope non si sposta al guscio
    // dell'applicazione, che e' l'ultimo passo di questa migrazione.
    <div className="cps-root">
      <div className="cloud-layer" aria-hidden="true">
        <div className="cloud c1"/><div className="cloud c2"/>
        <div className="cloud c3"/><div className="cloud c4"/>
        <div className="cloud c5"/><div className="cloud c6"/>
        <div className="cloud c7"/><div className="cloud c8"/>
        <div className="cloud c9"/><div className="cloud c10"/>
      </div>

      {/* NAV — barra unica, vedi components/SiteNav.jsx. Era la piu' povera
          delle tre copie: sei voci contro le otto della home. Le mancavano
          Roadmap e Chi siamo, oltre a EN/IT e al selettore di tema. "Chi
          siamo" non era sparita: non c'era mai stata (nav_about non compare in
          questo file in nessuno dei cinque commit che l'hanno toccato, dal 18
          agosto 2026). */}
      <SiteNav
        pagina="news"
        onVaiA={vaiA}
        onAbout={onAbout}
        onNews={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        onLogin={onLogin}
        onRegister={onRegister}
        tema={tema}
        onCambiaTema={cambiaTema}
      />

      {/* INTRO */}
      <section className="cps-hero" style={{ minHeight:'auto', paddingTop:160, paddingBottom:50 }}>
        <div className="cps-hero-badge">🆕 {t('nav_news')}</div>
        <h1 className="cps-hero-title" style={{ fontSize:'clamp(36px,6vw,64px)' }}>
          {t('page_title_line1')}<br/>
          <span className="cps-hero-green">{t('page_title_line2')}</span>
        </h1>
        <p className="cps-hero-sub" style={{ maxWidth:640 }}>
          {t('page_subtitle')}
        </p>
      </section>

      {/* UPDATES */}
      <section className="cps-section">
        <div className="cps-section-inner">
          <div className="cps-news-list">
            {ENTRIES.map((e, i) => (
              <div key={i} className="cps-news-item">
                <div className="cps-news-item-icon">{e.icon}</div>
                <div className="cps-news-item-body">
                  <div className="cps-news-item-meta">
                    <span className="cps-news-item-date">{t(`entry_${i}_date`)}</span>
                    <span className="cps-roadmap-status active"><span className="dot"/>{t('tag_new')}</span>
                  </div>
                  <h3>{t(`entry_${i}_title`)}</h3>
                  {/* Il corpo puo' contenere piu' paragrafi separati da una riga
                      vuota. Le voci storiche ne hanno uno solo e restano identiche:
                      lo split su riga vuota le lascia com'erano, e per quelle nuove
                      evita che i capoversi si incollino in un blocco unico. */}
                  {String(t(`entry_${i}_body`)).split(/\n\s*\n/).map((par, k) => (
                    <p key={k}>{par}</p>
                  ))}
                  {e.guide && (
                    <p className="cps-faq-guide">
                      <a href="/guida/mac" onClick={(ev) => { ev.preventDefault(); onApriGuida?.('mac'); }}>
                        → {t('guide:link_mac')}
                      </a>
                      <span aria-hidden="true"> · </span>
                      <a href="/guida/windows" onClick={(ev) => { ev.preventDefault(); onApriGuida?.('windows'); }}>
                        {t('guide:link_windows')}
                      </a>
                    </p>
                  )}
                  {e.bullets > 0 && (
                    <ul className="cps-news-item-bullets">
                      {Array.from({length: e.bullets}, (_, b) => (
                        <li key={b}>{t(`entry_${i}_bullet_${b+1}`)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="cps-section cps-section-dark">
        <div className="cps-section-inner cps-about-closing">
          <div className="cps-section-label">{t('closing_label')}</div>
          <h2 className="cps-section-title">{t('closing_title')}</h2>
          <p>{t('closing_body')}</p>
          <button className="cps-cta-primary" onClick={onLogin}>{t('closing_cta')}</button>
          {/* Invito a registrarsi (2026-09-02). "Registrati" e' uscito dalla
              barra su questa pagina, e il blocco di chiusura che c'era gia'
              parla solo a chi un account ce l'ha ("Accedi, vai su Profilo").
              Senza questa riga la pagina resterebbe senza nessun invito a
              iscriversi: stessa etichetta e stessa destinazione del bottone
              tolto dalla barra. */}
          <p className="cps-closing-register">{t('closing_register_line')}</p>
          <button className="cps-btn-register cps-closing-register-btn" onClick={onRegister}>{t('nav_register')}</button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="cps-footer">
        <div className="cps-footer-inner">
          <div className="cps-footer-brand">
            <span>♾️ LifetimeCloud.me</span>
            <p>{t('footer_tagline')}</p>
          </div>
          <div className="cps-footer-links">
            <button onClick={() => goHome('how-it-works')}>{t('nav_how_it_works')}</button>
            <button onClick={() => goHome('plans')}>{t('nav_plans')}</button>
            <button onClick={() => goHome('security')}>{t('nav_security')}</button>
            <button onClick={() => goHome('faq')}>{t('nav_faq')}</button>
            <button onClick={onLogin}>{t('nav_login')}</button>
            <button onClick={onRegister}>{t('nav_register')}</button>
            <button onClick={onTerms}>{t('common:footer_terms')}</button>
            <button onClick={onPrivacy}>{t('common:footer_privacy')}</button>
          </div>
        </div>
        <div className="cps-footer-bottom">
          <span>{t('footer_copyright')}</span>
          <span>{t('footer_security_line')}</span>
        </div>
      </footer>
    </div>
  );
}
